/**
 * Die Zeichensprache des A+S-Plans.
 *
 * Kennt weder DOM noch jsPDF noch React — und genau darum geht es.
 *
 * ═══ WARUM EINE ZWISCHENSPRACHE UND NICHT ZWEIMAL GEZEICHNET ══════════════
 * Jede Skizze dieses Moduls wird an zwei Stellen gebraucht: am Bildschirm
 * (und im Browserdruck der Aushaenge) als SVG, im Plan-PDF als jsPDF-Befehle.
 * Wer beides getrennt schreibt, hat zwei Zeichnungen, die anfangs gleich
 * aussehen und spaetestens beim dritten Aenderungswunsch auseinanderlaufen —
 * und die Abweichung faellt niemandem auf, weil nie jemand beide
 * nebeneinander sieht.
 *
 * Deshalb: eine reine Funktion je Skizzentyp erzeugt eine `Zeichnung`, also
 * eine Liste von Primitiven in MILLIMETERN. Zwei duenne Renderer setzen sie
 * um. Was die Zeichnung aussagt, ist damit ohne DOM testbar — man kann eine
 * Behauptung wie „der Pfeil zeigt aus dem Schwarzbereich heraus" pruefen,
 * ohne etwas zu rendern.
 *
 * ═══ WARUM MILLIMETER ═════════════════════════════════════════════════════
 * Weil das Ziel Papier ist. jsPDF rechnet in mm, ein SVG bekommt eine
 * `viewBox` in denselben Einheiten und skaliert verlustfrei auf A4 oder A3.
 * Pixel muessten an zwei Stellen umgerechnet werden, und eine der beiden
 * Umrechnungen waere irgendwann falsch.
 */

/** Farbrolle statt fester Farbe, damit Bildschirm und Druck dieselben
 *  Entscheidungen treffen und ein Aushang notfalls einfarbig bleiben kann. */
export type Farbrolle =
    | 'schwarzbereich'
    | 'graubereich'
    | 'weissbereich'
    | 'linie'
    | 'linie_stark'
    | 'text'
    | 'text_hell'
    | 'warnung'
    | 'hinweis'
    | 'flaeche_hell';

export type Primitive =
    | {
        art: 'rechteck';
        x: number; y: number; b: number; h: number;
        fuellung?: Farbrolle | string;
        linie?: Farbrolle | string;
        strich?: 'voll' | 'gestrichelt';
        radius?: number;
    }
    | {
        art: 'linie';
        x1: number; y1: number; x2: number; y2: number;
        linie?: Farbrolle | string;
        staerke?: number;
        strich?: 'voll' | 'gestrichelt';
    }
    | {
        art: 'pfeil';
        x1: number; y1: number; x2: number; y2: number;
        linie?: Farbrolle | string;
        staerke?: number;
        /** Laenge der Spitze in mm. */
        spitze?: number;
    }
    | {
        art: 'text';
        x: number; y: number; text: string;
        groesse: number;
        fett?: boolean;
        kursiv?: boolean;
        anker?: 'links' | 'mitte' | 'rechts';
        farbe?: Farbrolle | string;
    }
    | {
        art: 'masslinie';
        x1: number; y1: number; x2: number; y2: number;
        text: string;
    }
    | {
        /** Ein ISO-7010-Zeichen oder ein anderes Bild. `quelle` ist ein
         *  Pfad; das Laden ist Sache des Renderers. */
        art: 'bild';
        x: number; y: number; b: number; h: number;
        quelle: string;
        alt?: string;
    };

export interface Zeichnung {
    /** Zeichenflaeche in mm. */
    breite: number;
    hoehe: number;
    titel: string;
    untertitel?: string;
    teile: Primitive[];
    /** Erlaeuterungen unter der Zeichnung, z. B. die Farblegende. */
    legende?: { farbe: Farbrolle | string; text: string }[];
    /** Was die Zeichnung NICHT zeigt. Erscheint als Fussnote — eine Skizze,
     *  die etwas weglaesst, muss das sagen, sonst wird das Fehlende als
     *  „nicht vorhanden" gelesen. */
    fussnoten?: string[];
}

/**
 * Die Farbwerte der Rollen.
 *
 * Bewusst hier und nicht in einer CSS-Datei: das PDF hat kein CSS, und der
 * Aushang wird gedruckt. Beide Ausgaben lesen dieselbe Tabelle.
 */
export const FARBEN: Record<Farbrolle, string> = {
    schwarzbereich: '#1f2937',
    graubereich: '#9ca3af',
    weissbereich: '#ffffff',
    linie: '#64748b',
    linie_stark: '#0f172a',
    text: '#0f172a',
    text_hell: '#f8fafc',
    warnung: '#dc2626',
    hinweis: '#2563eb',
    flaeche_hell: '#f1f5f9',
};

/** Loest eine Farbrolle oder einen direkten Farbwert auf. Unbekannte Rollen
 *  werden zu Schwarz — sichtbar falsch ist besser als unsichtbar. */
export const farbe = (wert: Farbrolle | string | undefined, ersatz = '#0f172a'): string => {
    if (!wert) return ersatz;
    if (wert.startsWith('#')) return wert;
    return FARBEN[wert as Farbrolle] ?? '#000000';
};

// ═══════════════════════════════════════════════════════════════════════════
// HILFEN FUER DIE ZEICHENFUNKTIONEN
// ═══════════════════════════════════════════════════════════════════════════

/** Ein beschriftetes Rechteck — die haeufigste Figur dieses Moduls. */
export const kasten = (
    x: number, y: number, b: number, h: number,
    fuellung: Farbrolle | string,
    zeilen: { text: string; groesse: number; fett?: boolean; farbe?: Farbrolle | string }[],
    linie: Farbrolle | string = 'linie_stark',
): Primitive[] => {
    const teile: Primitive[] = [{ art: 'rechteck', x, y, b, h, fuellung, linie }];
    // Zeilen mittig stapeln: erst die Gesamthoehe, dann von oben setzen.
    const abstand = 1.6;
    const gesamt = zeilen.reduce((s, z) => s + z.groesse * 0.36 + abstand, -abstand);
    let cursor = y + h / 2 - gesamt / 2;
    for (const z of zeilen) {
        cursor += z.groesse * 0.36;
        teile.push({
            art: 'text', x: x + b / 2, y: cursor, text: z.text,
            groesse: z.groesse, fett: z.fett, anker: 'mitte', farbe: z.farbe ?? 'text',
        });
        cursor += abstand;
    }
    return teile;
};

/** Waagerechte Masslinie mit Text darueber. */
export const massWaagerecht = (
    x1: number, x2: number, y: number, text: string,
): Primitive[] => ([
    { art: 'masslinie', x1, y1: y, x2, y2: y, text },
]);

/**
 * Bricht einen Text an Wortgrenzen in Zeilen um.
 *
 * Bewusst nach ZEICHENZAHL und nicht nach gemessener Breite: die Messung
 * braeuchte einen Zeichensatz, den weder die Zeichenfunktion noch der Test
 * hat. Die Schaetzung ist grob, aber an beiden Ausgaben dieselbe — und ein
 * einheitlich zu frueh umgebrochener Kasten ist harmlos, ein je Renderer
 * anders umgebrochener nicht.
 *
 * Ein Wort, das laenger ist als die Zeile, bleibt ungetrennt stehen: lieber
 * ein Ueberstand als ein zerschnittenes Wort, das etwas anderes bedeutet.
 */
export const umbruch = (text: string, maxZeichen: number): string[] => {
    const worte = text.split(/\s+/).filter(Boolean);
    if (worte.length === 0) return [''];

    const zeilen: string[] = [];
    let laufend = '';
    for (const w of worte) {
        const probe = laufend ? `${laufend} ${w}` : w;
        if (probe.length > maxZeichen && laufend) {
            zeilen.push(laufend);
            laufend = w;
        } else {
            laufend = probe;
        }
    }
    if (laufend) zeilen.push(laufend);
    return zeilen;
};

/** Summe der Laengen — damit eine Skizze ihre Gesamtbreite nie doppelt
 *  gepflegt bekommt. */
export const summe = (werte: number[]): number => werte.reduce((s, w) => s + w, 0);

/** Sucht einen Texteintrag in einer Zeichnung. Nur fuer Tests und
 *  Fehlersuche gedacht — die Renderer brauchen das nicht. */
export const findeText = (z: Zeichnung, teil: string): Extract<Primitive, { art: 'text' }> | null =>
    (z.teile.find(t => t.art === 'text' && t.text.includes(teil)) as
        Extract<Primitive, { art: 'text' }> | undefined) ?? null;
